import { css } from '@emotion/react'
import { useSite } from 'api/goodtrust/site'
import { Icon } from 'components/icon'
import { PageTitle } from 'components/PageTitle'
import { BlockSpinner } from 'components/spinner/Spinner'
import { StoreContent } from 'components/store/storeContent/StoreContent'
import { StoreActionCard } from 'components/storeActionCard/StoreActionCard'
import { Tag } from 'components/Tag'
import { Text, Title, Title_h2_default } from 'components/Typography'
import { useTranslation } from 'next-i18next'
import { addCart, STORE_CART_SESSION_KEY, useStoreCart } from 'utils/cart'
import { formatPrice } from 'utils/format'
import { isSiteActionType, SiteActionTypeLowercase, useSiteActionSpecs } from 'utils/siteActions'

export function StoreSite({
  className,
  siteId,
  onClick,
}: {
  className?: string
  siteId: string
  onClick: () => void
}) {
  const { t } = useTranslation('device')
  const { t: tCommon } = useTranslation()
  const { data } = useSite(siteId)
  const cart = useStoreCart()

  const actions = useSiteActionSpecs()

  return (
    <StoreContent className={className}>
      {data?.json?.title && <PageTitle title={data?.json?.title} />}

      <Title variant={Title_h2_default} as="h2" css={{ marginBottom: '1.5rem' }}>
        {data?.json?.title
          ? t('device.order_site.title', { site: data?.json?.title })
          : t('device.order_site.title_no_site')}
      </Title>

      <Text
        css={css`
          max-width: 660px;
          margin-bottom: 30px;
        `}
      >
        {/* IMPROVE: [Localization] get requirement names. Not translated*/}
        GoodTrust will help you with this account, let us know which action you need help with
        today. After completing your order you'll need to prepare{' '}
        <strong css={{ color: 'var(--c-black)' }}>
          {data?.json?.site_requirements?.reduce<string>((memo, item, index) => {
            if (!item.requirement_name) return memo

            let result = ''

            if (!memo.length) {
              result = item.requirement_name
            } else {
              result = `${memo}, ${item.requirement_name}`
            }

            if (index + 1 >= (data?.json?.site_requirements?.length ?? 0)) {
              result += `, and `
            }

            return result
          }, '')}
          Power of Attorney
        </strong>{' '}
        for these actions.
      </Text>

      {data?.json ? (
        <div
          css={css`
            display: grid;
            grid-gap: 17px;
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          `}
        >
          {data?.json?.site_action_prices?.map((action) => {
            const action_type = action.action_type
            if (!isSiteActionType(action_type)) {
              return
            }

            const actionSpec = actions[action_type]

            const desc = tCommon(
              `common.site_actions.${
                action.action_type?.toLowerCase() as SiteActionTypeLowercase
              }.description`,
              {
                name: data?.json?.title,
              }
            )
            return (
              <StoreActionCard
                active={Boolean(siteId && cart.items?.[siteId]?.actionUuid === action.action_uuid)}
                key={action.action_uuid}
                data-testid={`site-action-item-${action.action_uuid}`}
                title={actionSpec.name}
                description={desc}
                price={
                  action.amount != null &&
                  action.currency != null && (
                    <Tag>
                      {(action.amount ?? 0) > 0
                        ? [formatPrice(action.amount), action.currency].join(' ')
                        : t('device.order_site.free_tag')}
                    </Tag>
                  )
                }
                onClick={() => {
                  if (
                    action.action_uuid != null &&
                    action.amount != null &&
                    action.currency != null &&
                    data.json?.uuid != null
                  ) {
                    void addCart({
                      cartItem: {
                        id: action.id,
                        actionUuid: action.action_uuid,
                        actionType: action.action_type,
                        amount: action.amount * 100,
                        currency: action.currency,
                        siteUuid: data.json?.uuid,
                        siteTitle: data.json.title,
                      },
                      type: STORE_CART_SESSION_KEY,
                    })

                    onClick()
                  }
                }}
                icon={<Icon name={actionSpec.icon} size="2rem" />}
              />
            )
          })}
        </div>
      ) : (
        <BlockSpinner />
      )}
    </StoreContent>
  )
}
